// =============================================================================
// features/crm/components/CrmFilterBar.tsx — Barra de filtros da lista do CRM
// (F58-S08: CRM-03, decisão D15).
//
// Controles: busca (nome ou telefone, nunca CPF), status, período de criação,
// "Sem agente" e "Parados". O estado vive na URL — o pai entrega `filters` já
// lido dela e recebe cada mudança como função `LeadUrlFilters -> LeadUrlFilters`
// (assim o debounce da busca nunca sobrescreve outro filtro com valor velho).
//
// "Sem cidade" só existe para quem tem `leads:triage` e NÃO refaz a fila: leva
// à visão Triagem do F55-S07, que é onde o lead sem cidade ganha destino.
// =============================================================================

import * as React from 'react';

import { Input } from '../../../components/ui/Input';
import { Select } from '../../../components/ui/Select';
import { SEARCH_MAX_LENGTH } from '../../../hooks/crm/leadFilters';
import type { LeadUrlFilters } from '../../../hooks/crm/leadFilters';
import type { LeadStatus } from '../../../hooks/crm/types';
import { withCreatedRange } from '../listFilters';

import { FilterToggleChip } from './FilterToggleChip';

const SEARCH_DEBOUNCE_MS = 300;

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'new', label: 'Novo' },
  { value: 'qualifying', label: 'Qualificando' },
  { value: 'simulation', label: 'Simulação' },
  { value: 'closed_won', label: 'Convertido' },
  { value: 'closed_lost', label: 'Perdido' },
  { value: 'archived', label: 'Arquivado' },
];

const FUNNEL_ICON = (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    className="w-3 h-3 shrink-0"
    aria-hidden="true"
  >
    <path d="M2 3h12l-4.5 5.5V13l-3 1.5V8.5z" />
  </svg>
);

interface CrmFilterBarProps {
  filters: LeadUrlFilters;
  onChange: (update: (current: LeadUrlFilters) => LeadUrlFilters) => void;
  /** Só com `leads:triage`: mostra "Sem cidade" e leva à visão Triagem. */
  canTriage: boolean;
  onOpenTriage: () => void;
}

export function CrmFilterBar({
  filters,
  onChange,
  canTriage,
  onOpenTriage,
}: CrmFilterBarProps): React.JSX.Element {
  // Texto digitado fica local (resposta imediata); a URL recebe após o debounce.
  const [text, setText] = React.useState(filters.search ?? '');
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // URL mudou por fora (remover etiqueta, limpar filtros): o campo acompanha.
  // `trim` evita apagar o espaço que a pessoa ainda está digitando.
  React.useEffect(() => {
    setText((prev) => (prev.trim() === (filters.search ?? '') ? prev : (filters.search ?? '')));
  }, [filters.search]);

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const handleSearch = (value: string): void => {
    setText(value);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const search = value.trim();
      onChange((f) => {
        const { search: _previous, ...rest } = f;
        return { ...rest, ...(search ? { search } : {}), page: 1 };
      });
    }, SEARCH_DEBOUNCE_MS);
  };

  const toggle = (key: 'no_agent' | 'stale'): void => {
    onChange((f) => {
      if (f[key]) {
        const { [key]: _off, ...rest } = f;
        return { ...rest, page: 1 };
      }
      return { ...f, [key]: true, page: 1 };
    });
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[220px]">
          <Input
            id="crm-search"
            type="search"
            placeholder="Buscar por nome ou telefone..."
            aria-label="Buscar por nome ou telefone"
            value={text}
            maxLength={SEARCH_MAX_LENGTH}
            onChange={(e) => handleSearch(e.target.value)}
            wrapperClassName="w-full"
          />
        </div>

        <div className="w-[160px]">
          <Select
            id="crm-status"
            aria-label="Filtrar por status"
            options={STATUS_OPTIONS}
            value={filters.status ?? ''}
            onChange={(e) => {
              const val = e.target.value as LeadStatus | '';
              onChange((f) => {
                const { status: _s, ...rest } = f;
                return { ...rest, ...(val ? { status: val } : {}), page: 1 };
              });
            }}
          />
        </div>

        <Input
          id="crm-created-from"
          type="date"
          label="Criados de"
          value={filters.created_from ?? ''}
          max={filters.created_to}
          onChange={(e) => onChange((f) => withCreatedRange(f, 'created_from', e.target.value))}
          wrapperClassName="w-[160px]"
        />
        <Input
          id="crm-created-to"
          type="date"
          label="até"
          value={filters.created_to ?? ''}
          min={filters.created_from}
          onChange={(e) => onChange((f) => withCreatedRange(f, 'created_to', e.target.value))}
          wrapperClassName="w-[160px]"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtros da lista">
        <FilterToggleChip
          label="Sem agente"
          pressed={filters.no_agent === true}
          onToggle={() => toggle('no_agent')}
          title="Leads sem responsável atribuído"
        />
        <FilterToggleChip
          label="Parados"
          pressed={filters.stale === true}
          onToggle={() => toggle('stale')}
          title="Sem interação há mais de 7 dias"
        />
        {canTriage && (
          <FilterToggleChip
            label="Sem cidade"
            pressed={false}
            onToggle={onOpenTriage}
            title="Leads sem cidade e sem responsável ficam na fila de triagem"
            icon={FUNNEL_ICON}
          />
        )}
      </div>
    </div>
  );
}
